import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill, { Quill } from 'react-quill';
import ImageResize from 'quill-image-resize-module-react';
import { ImageDrop } from 'quill-image-drop-module';
import 'react-quill/dist/quill.snow.css';
import './BlogForm.css';
import { blogService, api } from '../services';

// Register Quill modules once
if (typeof Quill !== 'undefined') {
  if (!Quill.imports['modules/imageResize']) {
    Quill.register('modules/imageResize', ImageResize);
  }
  if (!Quill.imports['modules/imageDrop']) {
    Quill.register('modules/imageDrop', ImageDrop);
  }
}

const generateSlug = (value) =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s\W-]+/g, '-')
    .replace(/^-+|-+$/g, '');

const resolveAssetUrl = (path) => {
  if (!path) {
    return '';
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const baseApiUrl = api?.defaults?.baseURL || 'http://localhost:5000/api';
  const assetBase = baseApiUrl.replace(/\/api\/?$/, '');

  return `${assetBase}/${path}`;
};

const isRichTextEmpty = (value) => {
  if (!value || typeof value !== 'string') {
    return true;
  }

  const textContent = value
    .replace(/<(.|\n)*?>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

  return textContent.length === 0;
};

const BlogForm = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const quillRef = useRef(null);

  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [chipInput, setChipInput] = useState('');
  const [metaKeywordInput, setMetaKeywordInput] = useState('');
  const [formState, setFormState] = useState({
    title: '',
    slug: '',
    description: '',
    coverImage: '',
    content: '',
    chips: [],
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [],
    isPublished: false,
  });

  const loadBlog = useCallback(async () => {
    if (mode !== 'edit' || !id) {
      return;
    }

    try {
      setLoading(true);
      const response = await blogService.getBlogById(id);
      const blog = response.data;

      setFormState({
        title: blog.title || '',
        slug: blog.slug || '',
        description: blog.description || '',
        coverImage: blog.coverImage || '',
        content: blog.content || '',
        chips: blog.chips || [],
        metaTitle: blog.metaTitle || '',
        metaDescription: blog.metaDescription || '',
        metaKeywords: blog.metaKeywords || [],
        isPublished: Boolean(blog.isPublished),
      });
      setMetaKeywordInput('');
      setSlugManuallyEdited(true);
    } catch (error) {
      console.error('Failed to load blog:', error);
      toast.error(error.response?.data?.message || 'Failed to load blog');
      navigate('/blogs');
    } finally {
      setLoading(false);
    }
  }, [id, mode, navigate]);

  useEffect(() => {
    loadBlog();
  }, [loadBlog]);

  const handleSlugChange = (value) => {
    setSlugManuallyEdited(true);
    setFormState((prev) => ({
      ...prev,
      slug: generateSlug(value),
    }));
  };

  const handleTitleChange = (event) => {
    const { value } = event.target;
    setFormState((prev) => ({
      ...prev,
      title: value,
      slug: slugManuallyEdited ? prev.slug : generateSlug(value),
    }));
  };

  const handleChipAdd = () => {
    const trimmed = chipInput.trim();
    if (!trimmed) {
      return;
    }

    setFormState((prev) => ({
      ...prev,
      chips: Array.from(new Set([...prev.chips, trimmed])),
    }));
    setChipInput('');
  };

  const handleChipDelete = (chip) => {
    setFormState((prev) => ({
      ...prev,
      chips: prev.chips.filter((item) => item !== chip),
    }));
  };

  const handleChipKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleChipAdd();
    }
  };

  const handleMetaKeywordAdd = () => {
    const trimmed = metaKeywordInput.trim();
    if (!trimmed) {
      return;
    }

    setFormState((prev) => ({
      ...prev,
      metaKeywords: Array.from(new Set([...(prev.metaKeywords || []), trimmed])),
    }));
    setMetaKeywordInput('');
  };

  const handleMetaKeywordDelete = (keyword) => {
    setFormState((prev) => ({
      ...prev,
      metaKeywords: (prev.metaKeywords || []).filter((item) => item !== keyword),
    }));
  };

  const handleMetaKeywordKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleMetaKeywordAdd();
    }
  };

  const handleCoverUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setUploadingCover(true);
      const response = await blogService.uploadImage(file);
      const imagePath = response.data?.path || response.data?.url;
      if (!imagePath) {
        throw new Error('Image upload failed');
      }

      setFormState((prev) => ({
        ...prev,
        coverImage: imagePath,
      }));
      toast.success('Cover image uploaded');
    } catch (error) {
      console.error('Cover image upload failed:', error);
      toast.error(error.response?.data?.message || 'Failed to upload cover image');
    } finally {
      setUploadingCover(false);
      event.target.value = '';
    }
  };

  const handleQuillImageUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      try {
        const response = await blogService.uploadImage(file);
        const imagePath = response.data?.url || response.data?.path;
        if (!imagePath) {
          throw new Error('Image upload failed');
        }

        const quill = quillRef.current?.getEditor();
        const range = quill?.getSelection(true);
        const insertUrl = resolveAssetUrl(imagePath);

        if (range) {
          quill.insertEmbed(range.index, 'image', insertUrl, 'user');
          quill.setSelection(range.index + 1);
        } else {
          quill.insertEmbed(quill.getLength(), 'image', insertUrl, 'user');
        }
      } catch (error) {
        console.error('Failed to upload image:', error);
        toast.error(error.response?.data?.message || 'Failed to upload image');
      }
    };
  }, []);

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ align: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: handleQuillImageUpload,
        },
      },
      imageResize: {
        parchment: Quill.import('parchment'),
        modules: ['Resize', 'DisplaySize', 'Toolbar'],
      },
      imageDrop: true,
    }),
    [handleQuillImageUpload]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedTitle = formState.title.trim();
    const trimmedDescription = formState.description.trim();
    const trimmedSlug = formState.slug.trim();
    const trimmedCoverImage = typeof formState.coverImage === 'string' ? formState.coverImage.trim() : '';

    if (!trimmedTitle) {
      toast.error('Title is required');
      return;
    }

    if (!trimmedDescription) {
      toast.error('Description is required');
      return;
    }

    if (isRichTextEmpty(formState.content)) {
      toast.error('Content is required');
      return;
    }

    if (!trimmedCoverImage) {
      toast.error('Cover image is required');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        title: trimmedTitle,
        slug: trimmedSlug,
        description: trimmedDescription,
        content: formState.content,
        coverImage: trimmedCoverImage,
        chips: formState.chips,
        metaTitle: formState.metaTitle,
        metaDescription: formState.metaDescription,
        metaKeywords: (formState.metaKeywords || []).map((item) => item.trim()).filter((item) => item),
        isPublished: formState.isPublished,
      };

      if (mode === 'edit' && id) {
        await blogService.updateBlog(id, payload);
        toast.success('Blog updated successfully');
      } else {
        await blogService.createBlog(payload);
        toast.success('Blog created successfully');
      }

      navigate('/blogs');
    } catch (error) {
      console.error('Failed to save blog:', error);
      toast.error(error.response?.data?.message || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Typography variant="h6">Loading blog...</Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate('/blogs')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {mode === 'edit' ? 'Edit Blog' : 'Create Blog'}
          </Typography>
        </Box>
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save Blog'}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              Content
            </Typography>
            <TextField
              label="Title"
              value={formState.title}
              onChange={handleTitleChange}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Slug"
              value={formState.slug}
              onChange={(event) => handleSlugChange(event.target.value)}
              fullWidth
              helperText="Auto-generated from the title. Adjust if needed."
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              value={formState.description}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, description: event.target.value }))
              }
              fullWidth
              required
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <Typography variant="subtitle1" mb={1}>
              Blog Content
            </Typography>
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={formState.content}
              onChange={(value) =>
                setFormState((prev) => ({
                  ...prev,
                  content: value,
                }))
              }
              modules={quillModules}
              className="blog-quill"
              placeholder="Start writing your blog content..."
              style={{ height: 320, marginBottom: 40 }}
            />
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              SEO Settings
            </Typography>
            <TextField
              label="Meta Title"
              value={formState.metaTitle}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, metaTitle: event.target.value }))
              }
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Meta Description"
              value={formState.metaDescription}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  metaDescription: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Add Meta Keyword"
              value={metaKeywordInput}
              onChange={(event) => setMetaKeywordInput(event.target.value)}
              onKeyDown={handleMetaKeywordKeyDown}
              onBlur={handleMetaKeywordAdd}
              fullWidth
              placeholder="Press Enter to add keyword"
              helperText="Meta keywords appear as chips below; press Enter or comma to add."
            />
            <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
              {formState.metaKeywords?.length ? (
                formState.metaKeywords.map((keyword) => (
                  <Chip
                    key={keyword}
                    label={keyword}
                    onDelete={() => handleMetaKeywordDelete(keyword)}
                    color="secondary"
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No meta keywords added yet.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              Publishing
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.isPublished}
                  onChange={(event) =>
                    setFormState((prev) => ({
                      ...prev,
                      isPublished: event.target.checked,
                    }))
                  }
                />
              }
              label={formState.isPublished ? 'Published' : 'Draft'}
            />
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              Cover Image
            </Typography>
            {formState.coverImage ? (
              <Box mb={2} position="relative">
                <Box
                  component="img"
                  src={resolveAssetUrl(formState.coverImage)}
                  alt="Cover"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DeleteIcon />}
                  onClick={() =>
                    setFormState((prev) => ({
                      ...prev,
                      coverImage: '',
                    }))
                  }
                  sx={{ mt: 1 }}
                >
                  Remove Cover
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" mb={2}>
                Upload a featured image to display on the blog list.
              </Typography>
            )}
            <Button variant="contained" component="label" disabled={uploadingCover}>
              {formState.coverImage ? 'Change Cover Image' : 'Upload Cover Image'}
              <input type="file" hidden accept="image/*" onChange={handleCoverUpload} />
            </Button>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              Chips / Tags
            </Typography>
            <TextField
              label="Add chip"
              value={chipInput}
              onChange={(event) => setChipInput(event.target.value)}
              onKeyDown={handleChipKeyDown}
              onBlur={handleChipAdd}
              fullWidth
              placeholder="Press Enter to add"
            />
            <Divider sx={{ my: 2 }} />
            <Box display="flex" gap={1} flexWrap="wrap">
              {formState.chips.map((chip) => (
                <Chip
                  key={chip}
                  label={chip}
                  onDelete={() => handleChipDelete(chip)}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {formState.chips.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No chips added yet.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BlogForm;

